import { Link as RouterLink } from 'react-router-dom';
import { Chip, Inline, Stack } from '@/ui';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Link as MuiLink, Text, Tooltip } from '@/ui/primitives';
import { linkTruncateSx } from '../cellContents/shared';
import { OverflowTooltip } from './OverflowTooltip';
import type { TableTextLine } from '../tableTypes';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

type TableTextCellProps = { primary: TableTextLine; secondary?: TableTextLine[] };
type RunLinkCellProps = {
  label: string;
  href?: string | null;
  latest?: boolean;
  onClick?: () => void;
  align?: 'left' | 'right';
};
type RelativeTimeTooltipCellProps = {
  value: Date | string | null | undefined;
  emptyLabel?: string;
};

const numericTextSx = {};

export const TableTextCell = ({ primary, secondary }: TableTextCellProps): ReactElement => {
  const secondaryLines = secondary ?? [];
  const primarySx = primary.sx ? (Array.isArray(primary.sx) ? primary.sx : [primary.sx]) : [];
  const primaryRole = primary.role ?? 'body';
  const primaryHref = primary.href;
  return (
    <Stack gap="sm" align="flex-start" sx={{ width: '100%', minWidth: 0 }}>
      {primaryHref ? (
        <Text role={primaryRole} sx={{ width: '100%', minWidth: 0 }}>
          <OverflowTooltip title={primary.label}>
            <MuiLink
              component={RouterLink}
              to={primaryHref}
              underline="hover"
              color="inherit"
              onClick={(event) => {
                event.stopPropagation();
                primary.onClick?.();
              }}
              sx={[linkTruncateSx, ...primarySx] as SxProps<Theme>}
            >
              {primary.label}
            </MuiLink>
          </OverflowTooltip>
        </Text>
      ) : (
        <OverflowTooltip title={primary.label}>
          <Text
            role={primaryRole}
            truncate
            sx={
              [
                { display: 'block', width: '100%', minWidth: 0, maxWidth: '100%' },
                ...primarySx,
              ] as SxProps<Theme>
            }
          >
            {primary.label}
          </Text>
        </OverflowTooltip>
      )}
      {secondaryLines.map((line) => (
        <Text
          key={`${line.href ?? 'static'}-${line.label}`}
          role={line.role ?? 'body'}
          color="textSecondary"
          truncate={!line.href}
          sx={{ width: '100%', minWidth: 0 }}
        >
          {line.href ? (
            <OverflowTooltip title={line.label}>
              <MuiLink
                component={RouterLink}
                to={line.href}
                underline="hover"
                color="inherit"
                onClick={(event) => {
                  event.stopPropagation();
                  line.onClick?.();
                }}
                sx={
                  [
                    linkTruncateSx,
                    ...(line.sx ? (Array.isArray(line.sx) ? line.sx : [line.sx]) : []),
                  ] as SxProps<Theme>
                }
              >
                {line.label}
              </MuiLink>
            </OverflowTooltip>
          ) : (
            <OverflowTooltip title={line.label}>
              <span>{line.label}</span>
            </OverflowTooltip>
          )}
        </Text>
      ))}
    </Stack>
  );
};

export const RunLinkCell = ({
  label,
  href,
  latest = false,
  onClick,
  align = 'left',
}: RunLinkCellProps): ReactElement => {
  const runNode = href ? (
    <OverflowTooltip title={label} inline>
      <MuiLink
        component={RouterLink}
        to={href}
        underline="hover"
        color="inherit"
        onClick={(event) => {
          event.stopPropagation();
          onClick?.();
        }}
        sx={[linkTruncateSx, numericTextSx]}
      >
        {label}
      </MuiLink>
    </OverflowTooltip>
  ) : (
    <OverflowTooltip title={label} inline>
      <Text role="body" component="span" sx={[linkTruncateSx, numericTextSx]}>
        {label}
      </Text>
    </OverflowTooltip>
  );

  return (
    <Inline
      gap="md"
      align="center"
      justify={align === 'right' ? 'flex-end' : 'flex-start'}
      sx={{ width: '100%', minWidth: 0, flexWrap: 'nowrap' }}
    >
      <Text role="body" component="span" sx={{ minWidth: 0, ...numericTextSx }}>
        {runNode}
      </Text>
      {latest ? <Chip label="Latest" tone="primary" transform="uppercase" size="xs" /> : null}
    </Inline>
  );
};

export const RelativeTimeTooltipCell = ({
  value,
  emptyLabel = '-',
}: RelativeTimeTooltipCellProps): ReactElement => {
  const timestamp = value ?? null;
  const label = timestamp ? formatRelativeTime(timestamp) : emptyLabel;
  const fullTimestamp = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
  return (
    <Tooltip
      title={fullTimestamp}
      disableHoverListener={!fullTimestamp}
      arrow
      slotProps={{
        tooltip: { sx: { fontSize: (theme) => theme.typography.body2.fontSize } },
      }}
    >
      <span>
        <Text role="body" component="span" sx={numericTextSx}>
          {label.length > 0 ? label : emptyLabel}
        </Text>
      </span>
    </Tooltip>
  );
};
